/**
 * E2E renderer test probe (Spec 4 / D2) — exposes the per-document startup
 * recorder to Playwright so a profiling run can close the document at the
 * STRICTER endpoint the harness can actually observe.
 *
 * Why a probe rather than an always-on mechanism: in normal operation the
 * document closes at overlay dismissal (`endpoint: 'overlay-ready'`), which is a
 * proxy — the 300ms fade has not finished and the toolbar manifest may still be
 * resolving. The real answer to "when can the producer act?" needs a DOM
 * observation (overlay actually faded AND the controls present and actionable),
 * and shipping an always-on DOM polling loop purely to collect a benchmark would
 * add cost to every user's boot to measure it. So the harness observes, and
 * hands the timestamp back through this probe.
 *
 * The gate mirrors terminal-outcome-probe.ts: `process.env` is NOT populated in
 * this renderer, so IRIS_E2E_TEST is read off `window.electron.process.env` (the
 * preload bridge). Production can never satisfy it, and Vite dead-code-eliminates
 * the body when the gate is a build-time constant.
 *
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
import {
  finishRendererDocument,
  getRendererLoadId,
  isRendererDocumentFinished,
  readRendererDiagnostics
} from '#~/boot/renderer-startup.js';
import type {
  RendererDestination,
  RendererDiagnosticRow,
  RendererEndpoint
} from '#~/boot/renderer-startup.js';

interface RendererStartupProbe {
  /** Close the document at an externally observed timestamp. Idempotent. */
  finish(input: {
    endMs: number;
    endpoint?: RendererEndpoint;
    destination?: RendererDestination;
    outcome?: 'usable' | 'error';
  }): void;
  /** Every row recorded in THIS document, including the summary once finished. */
  read(): readonly RendererDiagnosticRow[];
  /** This document's identity. Changes on every reload; boot_id does not. */
  loadId(): string;
  finished(): boolean;
}

const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.['IRIS_E2E_TEST'] === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  const probe: RendererStartupProbe = {
    finish: ({ endMs, endpoint = 'observed-controls', destination = 'room', outcome = 'usable' }) =>
      finishRendererDocument({ outcome, endMs, endpoint, destination }),
    read: () => readRendererDiagnostics(),
    loadId: () => getRendererLoadId(),
    finished: () => isRendererDocumentFinished()
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisRendererStartupProbe = probe;
}
